import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import extractText from 'flarum/common/utils/extractText';
import ExportLinkButton from './ExportLinkButton';
import FindingNoteModal from './FindingNoteModal';
import ListEnd from './ListEnd';
import FindingsSlideshow from '../utils/FindingsSlideshow';
import formatDate from '../utils/formatDate';
import PagedList from '../utils/PagedList';
import speciesUrl from '../utils/speciesUrl';

// As many as the discussion lists show at once: one page of them, then the
// next each time the end of the list comes near.
const PAGE_SIZE = 20;

// The API's maximum page.
const MAX_PAGE_SIZE = 500;
// Each order as the API sorts it (ExhibitionFindingResource::applyListOrder()).
const SORTS = {
  phylum: 'phylum',
  number: 'number',
  scientific: 'scientific',
  vernacular: 'vernacular',
  family: 'family',
  newest: '-createdAt',
  oldest: 'createdAt'
};

// Other extensions' own modules, read through flarum.reg when used rather
// than imported - see ExhibitionFindingEditModal's getSpeciesName() for why.

function speciesName(finding) {
  return finding.speciesDisplay()?.canonicalName || extractText(app.translator.trans('champis-exhibitions.forum.expo_page.undetermined'));
}

/**
 * /expo/:id - an exhibition's species for anyone, the way the champis-net
 * WordPress plugin shows them on a société's website: number, name (to its
 * species page), vernacular name, family and photos, in a lightbox. Search
 * and sort as the discussion lists have them, and the same Excel and PDF
 * downloads as the exhibition's own page. Nothing to edit: that's
 * ExhibitionPage, for the société's members.
 *
 * The species come a page at a time, the next one as the end of the list
 * comes near (PagedList, ListEnd), searched and sorted by the server
 * (ExhibitionFindingResource) - an exhibition of some 350 species shows its
 * first ones at once. "Rafraîchir" reloads the
 * exhibition and the list from its first page, as it's searched and sorted:
 * the species added since, during the exhibition.
 *
 * Read-only, so open to guests: an exhibition of a société that isn't
 * public (ExhibitionResource::scope()) is a 404 for them - same rule as the
 * plugin's API. Kept out of search engines server-side (Content\ExpoPage).
 */
export default class ExpoPage extends Page {
  exhibition = null;
  status = 'loading';
  sort = 'scientific';
  search = '';
  findings = (() => new PagedList('champis-exhibitions-findings', () => ({
    filter: {
      exhibition: this.exhibition.id(),
      ...(this.search.trim() ? {
        q: this.search.trim()
      } : {})
    },
    sort: SORTS[this.sort]
  }), {
    pageSize: PAGE_SIZE,
    maxPageSize: MAX_PAGE_SIZE
  }))();
  slideshow = (() => new FindingsSlideshow(this.findings, speciesName))();
  refreshing = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.findings.clear();
  }
  load() {
    this.status = 'loading';
    this.loadExhibition().then(exhibition => {
      // Its société's cards' order first: by phylum for a société sorting
      // them so, else by number if any.
      this.sort = exhibition.sortsByPhylum() ? 'phylum' : exhibition.hasNumberedFindings() ? 'number' : 'scientific';
      this.status = 'ready';
      app.setTitle([exhibition.name(), exhibition.societyName()].filter(Boolean).join(' - '));
      app.setTitleCount(0);
      this.findings.load();
    }).catch(error => {
      this.status = error.status === 404 ? 'notFound' : 'error';
    }).finally(() => m.redraw());
  }
  loadExhibition() {
    return app.store
    // Its own "not found" below rather than core's alert on top of it.
    .find('champis-exhibitions', m.route.param('id'), {}, {
      errorHandler: () => {}
    }).then(exhibition => this.exhibition = exhibition);
  }

  // The species added since: the exhibition again (its count, whether it
  // has photos now) and the list from its first page.
  refresh() {
    this.refreshing = true;
    this.loadExhibition().then(() => this.findings.load()).catch(() => app.alerts.show({
      type: 'error'
    }, app.translator.trans('champis-exhibitions.forum.expo_page.refresh_error'))).finally(() => {
      this.refreshing = false;
      m.redraw();
    });
  }
  view() {
    return m("div", {
      className: "Page ChampisExhibitionPage ChampisExpoPage"
    }, m("div", {
      className: "container"
    }, this.content()));
  }
  content() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.expo_page.${key}`, params);
    };
    if (this.status === 'loading') return m(LoadingIndicator, null);
    if (this.status === 'notFound') return m(Placeholder, {
      text: t('not_found')
    });
    if (this.status === 'error' || !this.exhibition) {
      return m("div", {
        className: "ChampisExpoPage-error"
      }, m(Placeholder, {
        text: t('error')
      }), m(Button, {
        className: "Button",
        onclick: () => this.load()
      }, t('retry_button')));
    }
    const exhibition = this.exhibition;
    const findings = this.findings;
    // What the rows shown were searched for.
    const searched = findings.currentParams.filter?.q;
    const count = exhibition.findingsCount() || 0;
    const start = formatDate(exhibition.startDate());
    const end = formatDate(exhibition.endDate());
    return [
    // The species pages' own title block, not a blue bar: the société
    // as the small line above, the name, dates and place muted below.
    m("div", {
      className: "SpeciesPage-header ChampisExpoPage-header"
    }, exhibition.societyName() && m("p", {
      className: "SpeciesPage-badges"
    }, exhibition.societyName().toUpperCase()), m("h1", null, exhibition.name()), m("p", {
      className: "SpeciesPage-vernacular"
    }, [end && end !== start ? `${start} – ${end}` : start, exhibition.location()].filter(Boolean).join(' · ')), exhibition.description() && m("p", {
      className: "ChampisExhibitionPage-description"
    }, exhibition.description()), count > 0 && m("div", {
      className: "ChampisExhibitionPage-metaActions"
    }, m(ExportLinkButton, {
      icon: "fa-file-excel",
      label: t('export_xlsx_button'),
      href: `${app.forum.attribute('apiUrl')}/champis-exhibitions/${exhibition.id()}/findings.xlsx`
    }), m(ExportLinkButton, {
      icon: "fa-file-pdf",
      label: t('export_pdf_button'),
      href: `${app.forum.attribute('apiUrl')}/champis-exhibitions/${exhibition.id()}/findings.pdf`
    }), exhibition.hasFindingPhotos() && m(Button, {
      className: "Button",
      icon: "fas fa-images",
      loading: this.slideshow.loading,
      onclick: () => this.slideshow.open()
    }, t('slideshow_button')))), m("div", {
      className: "ChampisExhibitionPage-findingsHeader"
    }, m("h3", null, t('findings_title', {
      count
    })), m("div", {
      className: "ChampisSocietyPage-sectionHeaderActions"
    }, count > 1 && this.sortDropdown(exhibition), m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-sync",
      "aria-label": extractText(t('refresh_button')),
      title: extractText(t('refresh_button')),
      loading: this.refreshing,
      onclick: () => this.refresh()
    }))), m("div", {
      className: "ChampisExhibitionPage-findingsBody"
    }, count > 0 && m("input", {
      className: "FormControl ChampisExhibitionPage-search",
      type: "search",
      placeholder: extractText(t('search_placeholder')),
      "aria-label": extractText(t('search_placeholder')),
      value: this.search,
      oninput: e => {
        this.search = e.target.value;
        this.findings.reloadSoon();
      }
    }), findings.items.length > 0 && m("ul", {
      className: "ChampisExhibitionPage-findings"
    }, findings.items.map(finding => this.row(finding))), findings.error ? m("div", {
      className: "ChampisExpoPage-error"
    }, m(Placeholder, {
      text: t('findings_error')
    }), m(Button, {
      className: "Button",
      onclick: () => findings.items.length > 0 ? findings.refresh() : findings.load()
    }, t('retry_button'))) : !findings.loading && findings.items.length === 0 && m(Placeholder, {
      text: searched ? t('search_no_results', {
        query: searched
      }) : t('findings_empty')
    })), m(ListEnd, {
      list: findings
    })];
  }

  // Same menu as the discussion lists' "Trier par…": the chosen order
  // ticked, a missing value (no number, an undetermined species…) last.
  sortDropdown(exhibition) {
    const keys = Object.keys(SORTS).filter(key => (key !== 'number' || exhibition.hasNumberedFindings()) && (key !== 'phylum' || exhibition.sortsByPhylum()));
    const label = key => app.translator.trans(`champis-exhibitions.forum.expo_page.sort.${key}`);
    return m(Dropdown, {
      buttonClassName: "Button",
      label: label(this.sort),
      accessibleToggleLabel: app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label')
    }, keys.map(key => m(Button, {
      icon: this.sort === key ? 'fas fa-check' : true,
      active: this.sort === key,
      onclick: () => {
        this.sort = key;
        this.findings.load();
      }
    }, label(key))));
  }
  row(finding) {
    const species = finding.speciesDisplay();
    const photos = finding.photos() || [];
    const meta = [finding.vernacularName(), species?.family].filter(Boolean);
    return m("li", {
      key: finding.id(),
      className: "ChampisExhibitionPage-finding"
    }, m("div", {
      className: "ChampisExhibitionPage-findingBody"
    }, m("div", {
      className: "ChampisExhibitionPage-findingName"
    }, finding.label() && m("span", {
      className: "ChampisExhibitionPage-findingNo"
    }, finding.label()), finding.gbifId() && species?.canonicalName ? m(Link, {
      href: speciesUrl(finding.gbifId(), species.canonicalName)
    }, this.speciesName(species)) : m("em", null, speciesName(finding)), finding.notes() && m(Button, {
      className: "Button Button--icon Button--link ChampisExpoPage-noteButton",
      icon: "fas fa-info-circle",
      "aria-label": extractText(app.translator.trans('champis-exhibitions.forum.expo_page.note_button', {
        name: speciesName(finding)
      })),
      title: extractText(app.translator.trans('champis-exhibitions.forum.expo_page.note_button', {
        name: speciesName(finding)
      })),
      onclick: () => app.modal.show(FindingNoteModal, {
        finding,
        name: speciesName(finding)
      })
    })), meta.length > 0 && m("div", {
      className: "ChampisExhibitionPage-findingMeta"
    }, meta.map(text => m("span", null, text)))), photos.length > 0 && m("div", {
      className: "ChampisExhibitionPage-findingPhotos"
    }, photos.map(photo => m("a", {
      key: photo.url,
      href: photo.url,
      "aria-label": extractText(app.translator.trans('champis-exhibitions.forum.expo_page.photo_label', {
        name: speciesName(finding)
      })),
      onclick: e => this.slideshow.open(e, finding, photo)
    }, m("img", {
      className: "ChampisExhibitionPage-findingPhoto",
      src: photo.thumbnailUrl || photo.url,
      alt: "",
      loading: "lazy"
    })))));
  }
  speciesName(species) {
    const format = window.flarum.reg.get('champis-species', 'forum/utils/speciesName');
    return format ? format(species) : m("em", null, species.canonicalName);
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/ExpoPage', ExpoPage);